import { FC, ReactNode, useState } from 'react';
import styled, { css } from 'styled-components';
import { SvgIcon } from '../SvgIcons/svgIcons';
import { theme } from '../theme';
import { HeadingComponent } from '@salecycle/ui-component-library';

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const TooltipContent = styled.div<{ variation: string }>`
  display: flex;
  word-break: break-word;
  line-height: 1.6;

  ${({ variation }) => {
    if (variation === 'tooltipDefualt') {
      return css`
        max-width: calc(350px - ${({ theme }) => theme.designToken.spacing4});
      `;
    } else if (variation === 'tooltipTitle') {
      return css`
        max-width: calc(205px - ${({ theme }) => theme.designToken.spacing4});
      `;
    } else {
      return css`
        max-width: calc(450px - ${({ theme }) => theme.designToken.spacing4});
      `;
    }
  }};
`;

const TooltipHeader = styled.div`
  display: flex;
`;

const TooltipBody = styled.div`
  margin-left: 8px;
`;

const Tooltip = styled.div<{ position: string; variation: string }>`
  border-radius: ${({ theme }) => theme.designToken.borderRadius1};
  background: ${({ theme }) => theme.designToken.baseWhite};
  padding: ${({ theme }) => theme.designToken.spacing3} ${({ theme }) => theme.designToken.spacing4};
  display: flex;
  flex: 1 0 0;
  position: absolute;
  z-index: 100;
  word-wrap: break-word;
  box-shadow: ${({ theme }) => theme.designToken.boxShadow1};
  flex-wrap: wrap;
  width: max-content;

  ${({ variation }) => {
    if (variation === 'tooltipDefualt') {
      return css`
        max-width: 350px;
      `;
    } else if (variation === 'tooltipTitle') {
      return css`
        max-width: 205px;
      `;
    } else {
      return css`
        max-width: 450px;
      `;
    }
  }};

  ${({ position, theme }) => {
    if (position === 'TopLeft' || position === 'TopRight') {
      return css`
        left: 50%;
        transform: translateX(-50%);
        bottom: 100%;
        margin-bottom: ${theme.designToken.spacing3};
      `;
    } else if (position === 'Right') {
      return css`
        left: 100%;
        top: 50%;
        transform: translateX(0) translateY(-50%);
        margin-left: ${theme.designToken.spacing3};
      `;
    } else if (position === 'Left') {
      return css`
        right: 100%;
        top: 50%;
        transform: translateX(0) translateY(-50%);
        margin-right: ${theme.designToken.spacing3};
      `;
    } else if (position === 'BottomLeft' || position === 'BottomRight') {
      return css`
        left: 50%;
        transform: translateX(-50%);
        top: 100%;
        margin-top: ${theme.designToken.spacing3};
      `;
    }
  }}

  &::before {
    content: ' ';
    border: solid transparent;
    position: absolute;
    pointer-events: none;
    width: 12px;
    height: 12px;
    transform: rotate(-45deg);
    background: ${({ theme }) => theme.designToken.baseWhite};

    ${({ position }) => {
      if (position === 'TopLeft') {
        return css`
          left: 24px;
          bottom: -6px;
        `;
      } else if (position === 'TopRight') {
        return css`
          right: 24px;
          bottom: -6px;
        `;
      } else if (position === 'Right') {
        return css`
          left: -6px;
          top: 50%;
          margin-top: -6px;
        `;
      } else if (position === 'Left') {
        return css`
          right: -6px;
          top: 50%;
          margin-top: -6px;
        `;
      } else if (position === 'BottomLeft') {
        return css`
          left: 24px;
          top: -6px;
        `;
      } else if (position === 'BottomRight') {
        return css`
          right: 24px;
          top: -6px;
        `;
      }
    }}
  }
`;

type toolTipPosition = 'Right' | 'Left' | 'TopLeft' | 'TopRight' | 'BottomLeft' | 'BottomRight';

export interface IProps extends IntrinsicProps {
  id?: string;
  label?: string;
  title?: string;
  children: ReactNode;
  position: toolTipPosition;
}

function getTooltipVariation(label: string, title: string) {
  if (label && !title) {
    return <TooltipContent variation={getVariation(label, title)}>{label}</TooltipContent>;
  }
  if (!label && title) {
    return (
      <TooltipContent variation={getVariation(label, title)}>
        <HeadingComponent variation="H3">{title}</HeadingComponent>
      </TooltipContent>
    );
  }
  if (label && title) {
    return (
      <TooltipContent variation={getVariation(label, title)}>
        <TooltipHeader>
          <SvgIcon name="circle-info" size="24" color={theme.designToken.primary700} />
        </TooltipHeader>
        <TooltipBody>
          <HeadingComponent variation="H3">{title}</HeadingComponent>
          {label}
        </TooltipBody>
      </TooltipContent>
    );
  }
}

function getVariation(label: string, title: string) {
  if (label && !title) {
    return 'tooltipDefualt';
  }
  if (!label && title) {
    return 'tooltipTitle';
  }
  if (label && title) {
    return 'tooltipIcon';
  }
}

export const TooltipComponent: FC<IProps> = ({ id, children, position, label, title }) => {
  let timeout;
  const [active, setActive] = useState(false);

  const showTip = () => {
    timeout = setTimeout(() => {
      setActive(true);
    }, 400);
  };

  const hideTip = () => {
    clearInterval(timeout);
    setActive(false);
  };

  return (
    <TooltipWrapper
      id={id}
      // When to show the tooltip
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
    >
      {children}
      {active && (
        <Tooltip position={position} variation={getVariation(label, title)}>
          {getTooltipVariation(label, title)}
        </Tooltip>
      )}
    </TooltipWrapper>
  );
};
